import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { onAutocompleteChangeInForm } from '@sharedCR/coreDesignSystem/form/AutocompleteCDS';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import _ from 'lodash';

import { MaritalStatusType } from 'constants/loanApplication';
import { maritalStatusOptions } from 'constants/options';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { getPersonFullName } from 'utils/loanApplication';

import {
  getLocationsCountiesAction,
  getStateSigningRequirements,
} from 'store/loanApplications/actions';
import {
  selectBorrowersTableData,
  selectIsCountiesExist,
  selectIsFHALoanType,
  selectIsRefinanceLoanPurposeFromApplication,
  selectIsSpouseSignatureIsRequired,
  selectIsSubjectPropertyPrimaryResidence,
  selectNonBorrowersTableData,
  selectSortedCountiesByStates,
} from 'store/loanApplications/selectors';

import { pagesPropTypes, SPOUSE_TYPE } from '../../MainContent.constants';

import { getPersonalInfoConfig } from './PersonalInfo.config';
import { wrapperStyles } from './PersonalInfo.styles';
import { getNbeOptionsFromForm, getNbeOptionsFromTableData } from './PersonalInfo.utils';

const PersonalInfo = ({
  changedFields,
  isLoading,
  isSaving,
  form,
  validationErrors,
  validateField,
  handleChange,
  entityId,
  section,
  setEntityConfig,
  setInForm,
  setMultipleInForm,
  className,
  setIsLoading,
  pendingChanges,
  disabledPendingChangesHighlight,
  applicationData,
  isNewEntityFlow,
}) => {
  const dispatch = useDispatch();

  const counties = useSelector(selectSortedCountiesByStates);
  const isCountiesExist = useSelector(selectIsCountiesExist);
  const isRefinanceFlow = useSelector(selectIsRefinanceLoanPurposeFromApplication);
  const isOccupancyPrimaryResidence = useSelector(selectIsSubjectPropertyPrimaryResidence);
  const borrowers = useSelector(selectBorrowersTableData);
  const nonBorrowersTableData = useSelector(selectNonBorrowersTableData);
  const isSpouseSignatureIsRequired = useSelector(selectIsSpouseSignatureIsRequired);
  const isFha = useSelector(selectIsFHALoanType);

  const prevRelationOptions = useRef([]);

  const entityFormData = form[entityId];
  const entityApplicationData = applicationData[entityId];
  const entityOrderNumber = isNewEntityFlow
    ? _.size(nonBorrowersTableData) + 1
    : entityApplicationData?.order;
  const entityState = entityFormData?.address?.state?.value;
  // current entity relationship fields
  const currentSpouseName = entityFormData?.relation?.name;
  const currentSpouseValue = entityFormData?.relation?.value;
  const isCurrentSpouseNbe = entityFormData?.relation?.type === SPOUSE_TYPE.NON_BORROWER;
  const isCurrentSpouseOther = currentSpouseValue === SPOUSE_TYPE.OTHER;
  const currentSpouseId = isCurrentSpouseOther ? null : entityFormData?.relation?.id;
  // spouse entity in form
  const spouseEntityFormData = form[currentSpouseId];
  const isSpouseEntityFormDataExistInForm = !!spouseEntityFormData;

  const isMarried = entityFormData?.maritalStatus?.value === MaritalStatusType.MARRIED;
  const isSpouseSignatureContext =
    isRefinanceFlow && isOccupancyPrimaryResidence && isSpouseSignatureIsRequired;
  const spouseEntityName = isSpouseEntityFormDataExistInForm
    ? getPersonFullName(spouseEntityFormData)
    : '';
  // rest variables
  const isCurrentSpouseNameDeprecated =
    spouseEntityName && !isCurrentSpouseOther && spouseEntityName !== currentSpouseName;
  const currentEntityFullName = getPersonFullName(entityFormData);
  const currentEntitySpouseValue = `${SPOUSE_TYPE.NON_BORROWER}${entityOrderNumber}`;

  const currentEntityRelationOption = useMemo(
    () => ({
      name: currentEntityFullName,
      value: currentEntitySpouseValue,
      id: entityId,
      type: SPOUSE_TYPE.NON_BORROWER,
    }),
    [currentEntityFullName, currentEntitySpouseValue, entityId],
  );

  const otherNonBorrowersMap = useMemo(
    () =>
      _.omit(
        isNewEntityFlow ? _.keyBy(nonBorrowersTableData, 'id') : { ...applicationData, ...form },
        entityId,
      ),
    [applicationData, form, entityId, isNewEntityFlow, nonBorrowersTableData],
  );

  const borrowerOptions = useMemo(
    () =>
      (borrowers ?? []).reduce((acc, borrower) => {
        const spouseType = borrower.spouse?.type;

        if (spouseType && !spouseType.startsWith(SPOUSE_TYPE.NON_BORROWER)) {
          return acc;
        }

        const borrowerValueAsSpouse = `${SPOUSE_TYPE.BORROWER}${borrower.orderNumber}`;

        const isCurrentBorrowerSpouseOfAnotherNbe = Object.values(otherNonBorrowersMap).some(
          ({ relation, spouse }) =>
            isNewEntityFlow
              ? spouse?.type === borrowerValueAsSpouse
              : relation === borrowerValueAsSpouse || relation?.value === borrowerValueAsSpouse,
        );

        if (isCurrentBorrowerSpouseOfAnotherNbe) {
          return acc;
        }

        const option = {
          name: borrower.fullName,
          value: `${SPOUSE_TYPE.BORROWER}${borrower.orderNumber}`,
          id: borrower.id,
          type: SPOUSE_TYPE.BORROWER,
        };

        acc.push(option);

        return acc;
      }, []),
    [borrowers, otherNonBorrowersMap, isNewEntityFlow],
  );

  const nbeOptions = useMemo(() => {
    return isNewEntityFlow
      ? getNbeOptionsFromTableData({
          nonBorrowers: nonBorrowersTableData,
        })
      : getNbeOptionsFromForm({ applicationData, form, entityId, currentEntitySpouseValue });
  }, [
    applicationData,
    currentEntitySpouseValue,
    entityId,
    form,
    isNewEntityFlow,
    nonBorrowersTableData,
  ]);

  const relationOptions = useMemo(() => {
    const mergedOptions = [
      ...borrowerOptions,
      ...nbeOptions,
      {
        name: 'Other',
        value: SPOUSE_TYPE.OTHER,
      },
    ];

    const isEqual = _.isEqual(mergedOptions, prevRelationOptions.current);

    if (!isEqual) {
      prevRelationOptions.current = mergedOptions;

      return mergedOptions;
    }

    return prevRelationOptions.current;
  }, [borrowerOptions, nbeOptions]);

  const filteredCounties = useMemo(() => {
    return entityState ? counties[entityState] : counties.ALL_COUNTIES;
  }, [counties, entityState]);

  useEffectSafeStrictMode(() => {
    setIsLoading(true);

    dispatch(getStateSigningRequirements()).finally(() => setIsLoading(false));
  }, [dispatch, setIsLoading]);

  useEffect(() => {
    if (!isCurrentSpouseNameDeprecated) {
      return;
    }

    // * need if we edited spouse name in current edit session
    setInForm(`[${entityId}].relation.name`, spouseEntityName, false);
  }, [spouseEntityName, entityId, isCurrentSpouseNameDeprecated, setInForm]);

  useEffectSafeStrictMode(() => {
    if (isCountiesExist) {
      return;
    }

    setIsLoading(true);

    dispatch(getLocationsCountiesAction())
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [dispatch, setIsLoading, isCountiesExist]);

  useEffect(() => {
    if (!currentSpouseId || !isCurrentSpouseNbe || isNewEntityFlow) {
      return;
    }

    const spousePersonConfig = getPersonalInfoConfig({
      entityId: currentSpouseId,
      section,
      isSpouseSignatureContext,
      relationOptions: [currentEntityRelationOption],
      isMarried: true,
      isConfigForSpouseEntity: true,
    });

    setEntityConfig(spousePersonConfig);
  }, [
    currentEntityRelationOption,
    isNewEntityFlow,
    isSpouseEntityFormDataExistInForm,
    isCurrentSpouseNbe,
    isSpouseSignatureContext,
    section,
    setEntityConfig,
    currentSpouseId,
  ]);

  useEffect(() => {
    if (
      !currentSpouseId ||
      !isCurrentSpouseNbe ||
      !isSpouseEntityFormDataExistInForm ||
      isNewEntityFlow
    ) {
      return;
    }

    setMultipleInForm(
      {
        [`[${currentSpouseId}].maritalStatus`]: maritalStatusOptions[0],
        [`[${currentSpouseId}].relation`]: currentEntityRelationOption,
      },
      false,
    );
  }, [
    isSpouseEntityFormDataExistInForm,
    isCurrentSpouseNbe,
    isNewEntityFlow,
    setMultipleInForm,
    currentSpouseId,
    currentEntityRelationOption,
  ]);

  const onRelationInputChange = useCallback(
    (e, option) => {
      onAutocompleteChangeInForm({ validateField, setInForm })(e, option);

      if (currentSpouseId && isCurrentSpouseNbe) {
        setMultipleInForm(
          {
            [`[${currentSpouseId}].maritalStatus`]: '',
            [`[${currentSpouseId}].relation`]: '',
          },
          false,
        );
      }

      if (isCurrentSpouseOther) {
        setMultipleInForm(
          {
            [`[${entityId}].spouseFirstName`]: '',
            [`[${entityId}].spouseLastName`]: '',
            [`[${entityId}].spouseEmail`]: '',
          },
          false,
        );
      }
    },
    [
      entityId,
      isCurrentSpouseOther,
      isCurrentSpouseNbe,
      setInForm,
      setMultipleInForm,
      currentSpouseId,
      validateField,
    ],
  );

  const onMaritalStatusInputChange = useCallback(
    (e, option) => {
      onAutocompleteChangeInForm({ validateField, setInForm })(e, option);

      if (option?.value !== MaritalStatusType.MARRIED && currentSpouseId) {
        setInForm(`[${entityId}].relation`, '', false);

        if (isCurrentSpouseOther) {
          setMultipleInForm(
            {
              [`[${entityId}].spouseFirstName`]: '',
              [`[${entityId}].spouseLastName`]: '',
              [`[${entityId}].spouseEmail`]: '',
            },
            false,
          );
        }

        if (isCurrentSpouseNbe) {
          setMultipleInForm(
            {
              [`[${currentSpouseId}].maritalStatus`]: '',
              [`[${currentSpouseId}].relation`]: '',
            },
            false,
          );
        }
      }
    },
    [
      entityId,
      isCurrentSpouseOther,
      isCurrentSpouseNbe,
      setInForm,
      setMultipleInForm,
      currentSpouseId,
      validateField,
    ],
  );

  const config = useMemo(
    () =>
      getPersonalInfoConfig({
        entityId,
        section,
        filteredCounties,
        isCurrentSpouseOther,
        isSpouseSignatureContext,
        isMarried,
        isFha,
        relationOptions,
        onRelationInputChange,
        onMaritalStatusInputChange,
      }),
    [
      entityId,
      section,
      filteredCounties,
      isSpouseSignatureContext,
      isCurrentSpouseOther,
      isMarried,
      isFha,
      relationOptions,
      onRelationInputChange,
      onMaritalStatusInputChange,
    ],
  );

  useEffect(() => {
    setEntityConfig(config);
  }, [config, setEntityConfig]);

  return (
    <Box className={className} sx={wrapperStyles}>
      <FormGenerator
        isNestedForm
        changedFields={changedFields}
        config={config}
        disabledPendingChangesHighlight={disabledPendingChangesHighlight}
        form={form}
        formName={FormNames.NON_BORROWER_INFO}
        handleChange={handleChange}
        isLoading={isSaving || isLoading}
        numberOfColumns={4}
        pendingChanges={pendingChanges}
        setInForm={setInForm}
        validateField={validateField}
        validationErrors={validationErrors}
      />
    </Box>
  );
};

PersonalInfo.propTypes = {
  ...pagesPropTypes,
};

export default memo(PersonalInfo);
